import { useEffect, useState } from 'react';

/**
 * 162: one debounce for every admin search box.
 *
 * Two pieces of state — what the operator has typed, and what has been asked for. The gap between
 * them is this hook. The platform's match is an unanchored substring scan, so a request per
 * keystroke runs six of them to type "SEED00".
 *
 * 🔴 This replaces a pattern that was copied per screen, and one copy was worse than the others:
 * `UserListPage` parked its timer on `window._emailTimer` — a GLOBAL name, so a second screen using
 * that pattern would have shared one timer with it. `CarListPage` already avoided it with a local
 * effect and left a comment saying why. Thirteen more search boxes made the extraction obvious.
 */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    // Cleared on every change and on unmount: without this the timer outlives its component and
    // publishes into a screen that is no longer there.
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return settled;
}
